import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Users, CheckCircle, XCircle, Shield, ShieldOff, UserPlus, Search, X } from 'lucide-react'
import { api } from '@/lib/api/client'
import type { EntraUserHit, PersonDirectoryEntry, User } from '@/types/api'

type Tab = 'directory' | 'forge'

export default function PeoplePage() {
  const queryClient = useQueryClient()
  const [tab,    setTab]    = useState<Tab>('directory')
  const [search, setSearch] = useState('')
  const [showAdd, setShowAdd] = useState(false)

  const { data: people = [], isLoading: peopleLoading } = useQuery({
    queryKey: ['people'],
    queryFn:  () => api.get<PersonDirectoryEntry[]>('/people'),
    staleTime: 5 * 60 * 1000,
  })

  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn:  () => api.get<User[]>('/users'),
  })

  const patchUser = useMutation({
    mutationFn: ({ id, ...body }: { id: number; isActive?: boolean; roles?: string | null; fortnoxEmployeeId?: string | null }) =>
      api.patch(`/users/${id}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  })

  const patchPerson = useMutation({
    mutationFn: ({ id, ...body }: { id: number; isOffshore?: boolean }) =>
      api.patch(`/people/${id}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['people'] }),
  })

  // ── Directory filter ─────────────────────────────────────────────────────
  // Manager name is searchable so a team lead can find their reports by typing
  // their own name (WI-11902).
  const filteredPeople = people.filter(p => {
    const q = search.toLowerCase()
    return (
      `${p.firstName} ${p.lastName}`.toLowerCase().includes(q) ||
      (p.email         ?? '').toLowerCase().includes(q) ||
      (p.jobTitle      ?? '').toLowerCase().includes(q) ||
      (p.department    ?? '').toLowerCase().includes(q) ||
      (p.reportsToName ?? '').toLowerCase().includes(q)
    )
  })

  const filteredUsers = users.filter(u => {
    const q = search.toLowerCase()
    return (
      u.displayName.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q)       ||
      (u.department ?? '').toLowerCase().includes(q)
    )
  })

  const activeCount   = people.filter(p => p.isActive).length
  const forgeCount    = users.length
  const linkedCount   = people.filter(p => p.forgeUserId !== null).length

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="page-eyebrow">People</div>
          <h1 className="page-title">Staff directory</h1>
          <div className="page-subtitle">Employees from Hailey HR · Forge access managed via Entra ID</div>
        </div>
      </div>

      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="stat">
          <div className="stat-label">Active employees</div>
          <div className="stat-value tabular">{activeCount}</div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>Synced from Hailey</div>
        </div>
        <div className="stat">
          <div className="stat-label">Forge accounts</div>
          <div className="stat-value tabular">{forgeCount}</div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>Have logged in</div>
        </div>
        <div className="stat">
          <div className="stat-label">Linked</div>
          <div className="stat-value tabular">{linkedCount}</div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>Hailey ↔ Entra matched</div>
        </div>
      </div>

      <div className="tabs">
        <button className={`tab${tab === 'directory' ? ' active' : ''}`} onClick={() => { setTab('directory'); setSearch('') }}>
          Directory <span className="count">{people.length}</span>
        </button>
        <button className={`tab${tab === 'forge' ? ' active' : ''}`} onClick={() => { setTab('forge'); setSearch('') }}>
          Forge access <span className="count">{users.length}</span>
        </button>
      </div>

      <div className="card" style={{ marginBottom: 0 }}>
        <div style={{
          padding: '12px 16px', borderBottom: '1px solid var(--ns-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <input
            type="text"
            className="search-box"
            placeholder={tab === 'directory' ? 'Search by name, email, title or department…' : 'Search by name or email…'}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: 340 }}
          />
          {tab === 'forge' && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setShowAdd(true)}
              title="Search Entra ID and pre-provision a Forge user without waiting for login"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <UserPlus size={14} /> Add user
            </button>
          )}
        </div>

        {tab === 'directory' && (
          <DirectoryTab rows={filteredPeople} loading={peopleLoading} onPatch={patchPerson.mutate} />
        )}
        {tab === 'forge' && (
          <ForgeAccessTab rows={filteredUsers} loading={usersLoading} onPatch={patchUser.mutate} />
        )}
      </div>

      {showAdd && (
        <AddUserModal
          onClose={() => setShowAdd(false)}
          onAdded={() => {
            queryClient.invalidateQueries({ queryKey: ['users']  })
            queryClient.invalidateQueries({ queryKey: ['people'] })
          }}
        />
      )}
    </div>
  )
}


// ── Directory tab ─────────────────────────────────────────────────────────────

function DirectoryTab({ rows, loading, onPatch }: {
  rows: PersonDirectoryEntry[]
  loading: boolean
  onPatch: (args: { id: number; isOffshore?: boolean }) => void
}) {
  if (loading) return <TableSpinner cols={9} />
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Email</th>
          <th>Title</th>
          <th>Department</th>
          <th title="Reporting manager — synced from Hailey HR">Manager</th>
          <th style={{ width: 130 }}>Type</th>
          <th style={{ width: 90, textAlign: 'center' }}>Forge</th>
          <th style={{ width: 90, textAlign: 'center' }} title="Drives OFFSHORE revenue category for this resource's hours (WI-11973)">Offshore</th>
          <th style={{ width: 80, textAlign: 'center' }}>Active</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(p => (
          <tr key={p.id}>
            <td style={{ fontWeight: 500 }}>{p.firstName} {p.lastName}</td>
            <td className="muted" style={{ fontSize: 12 }}>{p.email ?? '—'}</td>
            <td className="muted">{p.jobTitle ?? '—'}</td>
            <td className="muted">{p.department ?? '—'}</td>
            <td className="muted" style={{ fontSize: 12.5 }}>
              <ManagerCell entry={p} />
            </td>
            <td className="muted" style={{ fontSize: 12 }}>{fmtType(p.employmentType)}</td>
            <td style={{ textAlign: 'center' }}>
              {p.forgeUserId !== null ? (
                <ForgeChip roles={p.forgeRoles} active={p.forgeIsActive} />
              ) : (
                <span className="muted" style={{ fontSize: 11 }}>—</span>
              )}
            </td>
            <td style={{ textAlign: 'center' }}>
              <input
                type="checkbox"
                checked={p.isOffshore}
                onChange={e => onPatch({ id: p.id, isOffshore: e.target.checked })}
                title={p.isOffshore ? 'Subcontractor resource — hours resolve to OFFSHORE revenue' : 'Mark as offshore subcontractor'}
              />
            </td>
            <td style={{ textAlign: 'center' }}>
              {p.isActive
                ? <CheckCircle size={14} style={{ color: 'var(--ns-ok)' }} />
                : <XCircle    size={14} style={{ color: 'var(--ns-err)' }} />
              }
            </td>
          </tr>
        ))}
        {rows.length === 0 && (
          <tr><td colSpan={9} className="muted" style={{ textAlign: 'center', padding: 24 }}>
            No employees found
          </td></tr>
        )}
      </tbody>
    </table>
  )
}

// Manager cell — surfaces the Hailey-side name plus a faint chip when the
// manager hasn't been matched to a Forge user yet, because that's the exact
// condition under which this person won't appear in their manager's "My team"
// time view (the team scope keys off core.users.manager_id, which stays NULL
// until the manager logs in once). Title attr makes the cause explicit on
// hover. (WI-11902.)
function ManagerCell({ entry }: { entry: PersonDirectoryEntry }) {
  if (!entry.reportsToName) return <>—</>
  const noForge = entry.reportsToName != null && entry.managerId == null
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <span>{entry.reportsToName}</span>
      {noForge && (
        <span
          title="Hailey has this manager set, but they haven't logged into Forge yet — this person won't appear in their team time view until that happens."
          style={{
            fontSize: 10, padding: '1px 6px', borderRadius: 999,
            background: 'var(--ns-warn-soft)', color: 'var(--ns-warn)',
            fontWeight: 600, letterSpacing: 0.02,
          }}
        >
          no Forge account
        </span>
      )}
    </span>
  )
}


// ── Forge access tab ──────────────────────────────────────────────────────────

function ForgeAccessTab({
  rows, loading, onPatch,
}: {
  rows: User[]
  loading: boolean
  onPatch: (args: { id: number; isActive?: boolean; roles?: string | null; fortnoxEmployeeId?: string | null }) => void
}) {
  if (loading) return <TableSpinner cols={7} />
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Email</th>
          <th>Title / Dept</th>
          <th title="Reporting manager — synced from Hailey HR, not editable here">Manager</th>
          <th style={{ width: 160 }}>Roles</th>
          <th style={{ width: 110 }} title="Fortnox employee ID for payroll sync">Fortnox Emp.</th>
          <th style={{ width: 80, textAlign: 'center' }}>Active</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(u => (
          <tr key={u.id}>
            <td>
              <div style={{ fontWeight: 500 }}>{u.displayName}</div>
              <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                Last login: {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString('sv-SE') : '—'}
              </div>
            </td>
            <td className="muted" style={{ fontSize: 12 }}>{u.email}</td>
            <td className="muted" style={{ fontSize: 12 }}>
              {u.jobTitle   && <div>{u.jobTitle}</div>}
              {u.department && <div style={{ opacity: 0.7 }}>{u.department}</div>}
              {!u.jobTitle && !u.department && '—'}
            </td>
            <td className="muted" style={{ fontSize: 12 }} title="Synced from Hailey HR">
              {u.managerName ?? '—'}
            </td>
            <td>
              <RoleSelect
                value={u.roles}
                onChange={roles => onPatch({ id: u.id, roles })}
              />
            </td>
            <td>
              <FortnoxEmpInput
                value={u.fortnoxEmployeeId}
                onChange={val => onPatch({ id: u.id, fortnoxEmployeeId: val })}
              />
            </td>
            <td style={{ textAlign: 'center' }}>
              <button
                className="btn btn-ghost btn-icon btn-sm"
                title={u.isActive ? 'Deactivate Forge access' : 'Reactivate Forge access'}
                onClick={() => onPatch({ id: u.id, isActive: !u.isActive })}
              >
                {u.isActive
                  ? <CheckCircle size={14} style={{ color: 'var(--ns-ok)' }} />
                  : <XCircle    size={14} style={{ color: 'var(--ns-err)' }} />
                }
              </button>
            </td>
          </tr>
        ))}
        {rows.length === 0 && (
          <tr><td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 24 }}>
            No Forge users yet — staff appear here after their first login
          </td></tr>
        )}
      </tbody>
    </table>
  )
}


// ── Small components ──────────────────────────────────────────────────────────

function ForgeChip({ roles, active }: { roles: string | null; active: boolean | null }) {
  const isAdmin = (roles ?? '').includes('Admin')
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
      {isAdmin
        ? <span title="Admin"><Shield    size={13} style={{ color: 'var(--ns-brand)' }} /></span>
        : <span title="User"><Users      size={13} style={{ color: 'var(--ns-text-muted)' }} /></span>
      }
      {active === false && (
        <span title="Forge access disabled"><ShieldOff size={12} style={{ color: 'var(--ns-err)' }} /></span>
      )}
    </div>
  )
}

const KNOWN_ROLES = ['Admin', 'Payroll', 'Time Administrator']

function RoleSelect({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  const activeRoles = (value ?? '').split(',').map(r => r.trim()).filter(Boolean)

  const toggle = (role: string) => {
    const next = activeRoles.includes(role)
      ? activeRoles.filter(r => r !== role)
      : [...activeRoles, role]
    onChange(next.join(',') || null)
  }

  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {KNOWN_ROLES.map(role => {
        const active = activeRoles.includes(role)
        return (
          <button
            key={role}
            onClick={() => toggle(role)}
            title={active ? `Remove ${role}` : `Add ${role}`}
            style={{
              fontSize: 11, padding: '2px 7px', borderRadius: 4, cursor: 'pointer',
              border: '1px solid',
              borderColor: active ? 'var(--ns-brand-strong)' : 'var(--ns-border)',
              background:  active ? 'var(--ns-brand-soft)' : 'transparent',
              color:       active ? 'var(--ns-brand-strong)' : 'var(--ns-border)',
              fontWeight:  active ? 600 : 400,
              opacity:     active ? 1 : 0.4,
            }}
          >
            {role}
          </button>
        )
      })}
    </div>
  )
}

function FortnoxEmpInput({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  const [editing, setEditing] = useState(false)
  const [draft,   setDraft]   = useState('')

  if (!editing) {
    return (
      <button
        className="btn btn-ghost btn-sm"
        style={{ fontFamily: 'JetBrains Mono', fontSize: 12, minWidth: 80, justifyContent: 'flex-start' }}
        onClick={() => { setDraft(value ?? ''); setEditing(true) }}
        title="Fortnox employee ID — used for payroll sync"
      >
        {value ? value : <span className="muted">Set ID…</span>}
      </button>
    )
  }

  return (
    <input
      autoFocus
      type="text"
      className="input mono"
      style={{ width: 90, fontSize: 12 }}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => {
        onChange(draft.trim() || null)
        setEditing(false)
      }}
      onKeyDown={e => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
        if (e.key === 'Escape') setEditing(false)
      }}
      placeholder="e.g. 1"
    />
  )
}

// ── Add user modal ────────────────────────────────────────────────────────────
// Searches Entra ID via the backend (which already filters out OIDs that exist
// in core.users) and POSTs the chosen hit back to /users so the row exists
// without waiting for that person's first login.

function AddUserModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [query,   setQuery]   = useState('')
  const [debounced, setDebounced] = useState('')
  const [pendingOid, setPendingOid] = useState<string | null>(null)
  const [addError, setAddError] = useState<string | null>(null)

  // 250ms debounce so we don't fire a Graph round-trip on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250)
    return () => clearTimeout(t)
  }, [query])

  const { data: hits = [], isFetching, error: searchError } = useQuery({
    queryKey: ['entra-search', debounced],
    queryFn:  () => api.get<EntraUserHit[]>(`/users/entra/search?q=${encodeURIComponent(debounced)}`),
    enabled:  debounced.length >= 2,
    staleTime: 30_000,
  })

  const addMutation = useMutation({
    mutationFn: (hit: EntraUserHit) =>
      api.post<User>('/users', {
        entraOid:    hit.oid,
        email:       hit.email,
        displayName: hit.displayName,
      }),
    onMutate: hit => { setPendingOid(hit.oid); setAddError(null) },
    onSuccess: () => { setPendingOid(null); onAdded(); onClose() },
    onError:   (e: Error) => { setPendingOid(null); setAddError(e.message) },
  })

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{ width: 520, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{
          padding: '12px 16px', borderBottom: '1px solid var(--ns-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ fontWeight: 600 }}>Add Forge user</div>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose} title="Close"><X size={14} /></button>
        </div>

        <div style={{ padding: 16, borderBottom: '1px solid var(--ns-border)' }}>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: 9, opacity: 0.6 }} />
            <input
              autoFocus
              type="text"
              className="input"
              placeholder="Search Entra by name or email…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              style={{ width: '100%', paddingLeft: 30 }}
            />
          </div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>
            Pre-provisions a Forge account so the user appears in lists before they log in.
            Roles can be set after they're added.
          </div>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {debounced.length < 2 && (
            <div className="muted" style={{ padding: 24, textAlign: 'center', fontSize: 12.5 }}>
              Type at least 2 characters to search.
            </div>
          )}
          {debounced.length >= 2 && isFetching && (
            <div className="muted" style={{ padding: 24, textAlign: 'center', fontSize: 12.5 }}>
              Searching Entra…
            </div>
          )}
          {searchError && (
            <div style={{ padding: 16, color: 'var(--ns-err)', fontSize: 12.5 }}>
              {(searchError as Error).message}
            </div>
          )}
          {debounced.length >= 2 && !isFetching && !searchError && hits.length === 0 && (
            <div className="muted" style={{ padding: 24, textAlign: 'center', fontSize: 12.5 }}>
              No new Entra users match — anyone matching may already have a Forge account.
            </div>
          )}
          {hits.map(hit => (
            <div
              key={hit.oid}
              style={{
                padding: '10px 16px', borderBottom: '1px solid var(--ns-border)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 500 }}>{hit.displayName}</div>
                <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>{hit.email}</div>
              </div>
              <button
                className="btn btn-primary btn-sm"
                disabled={pendingOid === hit.oid}
                onClick={() => addMutation.mutate(hit)}
              >
                {pendingOid === hit.oid ? 'Adding…' : 'Add'}
              </button>
            </div>
          ))}
        </div>

        {addError && (
          <div style={{ padding: '8px 16px', color: 'var(--ns-err)', fontSize: 12.5, borderTop: '1px solid var(--ns-border)' }}>
            {addError}
          </div>
        )}
      </div>
    </div>
  )
}


function TableSpinner({ cols }: { cols: number }) {
  return (
    <table className="table"><tbody>
      <tr><td colSpan={cols} className="muted" style={{ textAlign: 'center', padding: 32 }}>Loading…</td></tr>
    </tbody></table>
  )
}

function fmtType(t: string | null): string {
  if (!t) return '—'
  return t.replace(/([A-Z])/g, ' $1').trim()
}
